<script setup lang="ts">
import type { MDCParserResult } from "@nuxtjs/mdc"

import { parseMarkdown } from "@nuxtjs/mdc/runtime"
import rehypeKatex from "rehype-katex"
import remarkMath from "remark-math"
import { visit } from "unist-util-visit" // cSpell:disable-line

import { StaticFile } from "~/classes/file/StaticFile"
import FileDetailsRef from "~/components/Agent/File/AgentFileCard.vue"
import FileRef from "~/components/Agent/File/AgentFileInline.vue"
import ProseA from "~/components/Agent/Output/Markdown/ProseA.vue"
import ProsePre from "~/components/Agent/Output/Markdown/ProsePre.vue"

const props = defineProps({
  output: null
})

const { addFileReference } = useAgent()

// the agent needs to merge all of its text and file ref fragments into a single
// markdown string, but leave the other fragment types alone (like `searchViewRef`)

const instance = () => {
  return (tree: any) => {
    // remove all html and convert it to "inlineCode"
    visit(tree, node => node?.type === "html", (node) => {
      node.type = "inlineCode"
    })
    // converts any component-like syntax (e.g. `::custom{test=Hello}`) to a text node with `:name` format unless it is in our whitelist
    visit(tree, node => node?.type === "textComponent", (node) => {
      if (["file-ref", "file-details-ref"].includes(node.name))
        return
      node.type = "text"
      node.value = `:${node.name}`
      delete node.data
      delete node.fmAttributes
      delete node.name
    })
    // converts any containerComponent (e.g. `::custom{test=Hello}`) to a text node with `::name::` format unless it is in our whitelist
    visit(tree, node => node?.type === "containerComponent", (node) => {
      node.type = "paragraph"
      node.children = [
        { type: "text", value: `::${node.name}::` },
      ]
      delete node.data
      delete node.fmAttributes
      delete node.name
      delete node.attributes
    })
  }
}

const markdown = (text: string[]) => parseMarkdown(text.join(""), {
  remark: {
    plugins: {
      whitelist: {
        instance,
      },
      math: {
        instance: remarkMath,
      },
    },
  },
  rehype: {
    plugins: {
      katex: {
        instance: rehypeKatex,
      },
    },
  },
})

const fragments: Ref<(AgentOutputTurnFragment | { markdown: MDCParserResult })[]> = ref([])

watchImmediate(() => props.output, async (output) => {
  const allFragments: (AgentOutputTurnFragment | { markdown: MDCParserResult })[] = []
  let currentTextFragment: string[] = []
  for (const fragment of output.fragments) {
    // The 'unused' variant
    if (typeof fragment === "string") {
      if (fragment !== "unused")
        console.error("Unexpected string fragment in agent output", fragment)
      continue
    }
    if ("text" in fragment) {
      currentTextFragment.push(fragment.text)
    }
    else if ("fileRef" in fragment) {
      addFileReference(new StaticFile(fragment.fileRef.file))
      const fileId = fragment.fileRef.file.id
      currentTextFragment.push(` :file-ref{fileId="${fileId}"} `)
    }
    else if ("fileDetailsRef" in fragment) {
      addFileReference(new StaticFile(fragment.fileDetailsRef.file))
      const fileId = fragment.fileDetailsRef.file.id
      const properties = fragment.fileDetailsRef.properties.join(",")
      currentTextFragment.push(` :file-details-ref{fileId=${fileId} properties=${properties}} `)
    }
    else {
      if (currentTextFragment.length > 0) {
        // flush the current text fragment before adding the non-text fragment
        allFragments.push({ markdown: await markdown(currentTextFragment) })
        currentTextFragment = []
      }
      allFragments.push(fragment)
    }
  }
  if (currentTextFragment.length > 0) {
    // flush the last text fragment if there is one
    allFragments.push({ markdown: await markdown(currentTextFragment) })
  }

  fragments.value = allFragments
})
</script>

<template>
  <div class="panel-agent-output-container">
    <template v-for="(fragment, key) in fragments" :key="key">
      <span v-if="typeof fragment === 'string'" />
      <span v-else-if="'markdown' in fragment" class="token">
        <!-- @vue-expect-error weird typing -->
        <m-d-c-renderer
          :body="fragment.markdown.body"
          :data="fragment.markdown.data"
          :components="{
            FileRef,
            FileDetailsRef,
            pre: ProsePre,
            a: ProseA,
          }"
          :prose="false"
          class="agent-output-markdown"
        />
      </span>
      <div v-else>
        ???
      </div>
    </template>
  </div>
</template>

<style lang="sass" scoped>
@import '~/assets/styles/generated/variables.sass'

.panel-agent-output-container
  @apply mx-[#{$panel-p-default}]
  @apply t-panel-base leading-[#{$global-lh-lg}] text-$global-t-primary
  @apply select-text
</style>

<style lang="sass">
@import '~/assets/styles/generated/variables.sass'

// This styling is not scoped since vue can't apply styling to a dynamic markdown
// component. So instead we must scope everything to this class
.agent-output-markdown
  @apply t-agent-chat
  hr
    @apply border-$global-steel-secondary border-t-0.75 my-3
  code
    @apply text-$global-red-primary fw-[500] bg-$global-steel-accent rounded-sm px-0.5 -mx-0.5
  h1
    @apply text-size-$page-properties-2xl-fs fw-bold tracking-$page-properties-2xl-ls leading-$page-properties-2xl-lh
    &>code
      @apply px-1.25 py-0.25 rounded-sm fw-semibold
  * + h1
    @apply mt-4
  h2
    @apply text-size-$page-properties-xl-fs fw-bold tracking-$page-properties-xl-ls leading-$page-properties-xl-lh
    &>code
      @apply px-0.75 py-0.25 rounded-sm fw-semibold
  * + h2
    @apply mt-3
  h3
    @apply text-size-$page-properties-lg-fs fw-bold tracking-$page-properties-lg-ls leading-$page-properties-lg-lh
    &>code
      @apply px-0.75 py-0.25 rounded-sm fw-semibold
  * + h3
    @apply mt-2
  h4
    @apply text-size-$page-properties-md-fs fw-bold tracking-$page-properties-md-ls leading-$page-properties-md-lh
    &>code
      @apply px-0.5 py-0.25 rounded-sm fw-semibold
  * + h4
    @apply mt-1.5
  h5
    @apply text-size-$page-properties-sm-fs fw-bold tracking-$page-properties-sm-ls leading-$page-properties-sm-lh
    &>code
      @apply px-0.5 py-0.25 rounded-sm fw-semibold
  * + h5
    @apply mt-1
  h6
    @apply text-size-$page-properties-xs-fs fw-bold tracking-$page-properties-xs-ls leading-$page-properties-xs-lh
    &>code
      @apply px-0.5 py-0.25 rounded-sm fw-semibold
  * + h6
    @apply mt-1
  p
    @apply break-words
  * + p
    @apply mt-1.5
  strong
    @apply font-bold
  p>code
    @apply px-0.5 py-0.25 rounded-sm
  p>.file-inline, ul .file-inline, ol .file-inline
    @apply text-size-$global-fs-sm fw-[400]
  blockquote
    @apply border-l-1.5 border-$global-steel-secondary pl-2.25 italic text-$global-t-secondary
  table
    border-spacing: 0
    border-collapse: separate
    border-radius: 4px
    @apply max-w-full overflow-x-auto block my-2
    td, th
      @apply border border-$global-steel-secondary px-2 py-1.5
    tr:hover
      @apply bg-$global-bg-secondary-hover
    thead:first-child tr:first-child th:first-child
      border-radius: 6px 0 0 0
    thead:first-child tr:first-child th:last-child
      border-radius: 0 6px 0 0
    tbody:last-child tr:last-child td:first-child
      border-radius: 0 0 0 6px
    tbody:last-child tr:last-child td:last-child
      border-radius: 0 0 6px 0

  ul
    @apply pl-4 space-y-1
    li
      @apply list-disc list-outside
  * + ul
    @apply mt-2
  ul + *
    @apply mt-2

  ol
    @apply pl-4 space-y-1
    li
      @apply list-decimal list-outside
  * + ol
    @apply mt-2
  ol + *
    @apply mt-2

  pre
    code
      @apply bg-transparent
</style>
